import { Component, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation, inject } from '@angular/core';
import {FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {firstValueFrom, skip, Subject, take, takeUntil} from 'rxjs';
import {ActiveOrder} from '../../../../generic/classes/active.order';
import {BarCode} from '../../../../generic/classes/barcode';
import {DeliveryAddress} from '../../../../generic/classes/deliveryaddress';
import {Message} from '../../../../generic/classes/message';
import {ShoppingCartLine} from '../../../../generic/classes/shoppingcartline';
import {User} from '../../../../generic/classes/user';
import {OrderTranslationsComponent} from '../../../../generic/component/my/order.translations.component';
import {Logger} from '../../../../generic/logging/default-log.service';
import {ActiveOrderService} from '../../../../generic/service/active.order.service';
import {ApplicationService} from '../../../../generic/service/application.service';
import {DeliveryService} from '../../../../generic/service/delivery.service';
import {ShoppingCartService} from '../../../../generic/service/shoppingcart.service';
import {UserService} from '../../../../generic/service/user.service';
import {BarcodeService} from '../../../../service/barcode.service';
import {AddBarcodeRowComponent} from './add.barcode.row.component';
import {NumberFormatPipe} from '../../../../generic/pipes/number.format.pipe';
import {CustomDatePipe} from '../../../../generic/pipes/custom.date.pipe';
import {DynamicModalDirective} from '../../../../generic/component/modal/dynamic.modal.directive';
import {ShoppingCartRowComponent} from '../../../products/shoppingcart/shopping.cart.row';
import {PendingOrdersDetailSortComponent} from './pending.order.detail.sort.component';
import {NgClass} from '@angular/common';
import {SalespersonTelephoneComponent} from '../../../../generic/component/my/salesperson.telephone.component';

@Component({
	selector: 'pending-order-detail',
	styleUrls: [
		'../../../../../assets/style/_order-overview.scss',
		'pending.order.detail.component.scss',
		'../../../../../assets/style/_layout.scss',
	],
	encapsulation: ViewEncapsulation.None,
	templateUrl: 'pending.order.detail.component.html',
	standalone: true,
	imports: [
		OrderTranslationsComponent,
		RouterLink,
		SalespersonTelephoneComponent,
		FormsModule,
		ReactiveFormsModule,
		NgClass,
		PendingOrdersDetailSortComponent,
		ShoppingCartRowComponent,
		AddBarcodeRowComponent,
		DynamicModalDirective,
		CustomDatePipe,
		NumberFormatPipe,
	],
})
export class PendingOrderDetailComponent implements OnInit, OnDestroy {
	private shoppingCartService = inject(ShoppingCartService);
	private barcodeService = inject(BarcodeService);
	private deliveryService = inject(DeliveryService);
	private activatedRoute = inject(ActivatedRoute);
	private activeOrderService = inject(ActiveOrderService);
	private userService = inject(UserService);
	private logger = inject(Logger);
	private router = inject(Router);
	applicationService = inject(ApplicationService);

	@ViewChild(OrderTranslationsComponent) orderTranslationsComponent: OrderTranslationsComponent;
	@ViewChildren('addbarcode') barcodeRows: QueryList<AddBarcodeRowComponent>;

	shoppingCartLines: Array<ShoppingCartLine>;
	lastShoppingCartLines: Array<ShoppingCartLine>;
	confirmAllButtonDisabled: boolean = false;
	confirmAllLoading: boolean = false;
	shipmentDate: Date;
	deliveryAddress: DeliveryAddress;
	deliveryAddressCode: string;
	deliveryDate: number;
	confirmed: boolean = false;
	showBarcode: boolean = false;
	isBarcodeEnabled: boolean = false;
	allowComments: boolean;
	closingInHours: number;
	closingTimeString: string;
	factorForm: UntypedFormGroup;
	referenceForm: UntypedFormGroup;
	customerReference: string;
	sortingKey: string = 'inserted';
	sortingDirection: string = 'ASC';
	totalPrice: number = 0;
	totalVolume: number = 0;
	totalVolumeCut: number = 0;
	totalVolumeMisc: number = 0;
	totalVolumePlants: number = 0;
	lastConfirmedActiveOrder: ActiveOrder | null;
	private unsubscribe$: Subject<void> = new Subject<void>();

	constructor() {
		const formBuilder = inject(UntypedFormBuilder);

		this.factorForm = formBuilder.group({
			factor: '1.00',
			rounding: '0.01'
		});

		this.referenceForm = formBuilder.group({
			reference: '',
		});
	}

	/**
	 * Subscribe to the params in the activated route and init the values correctly
	 */
	ngOnInit() {
		this.activatedRoute.params
			.pipe(take(1))
			.subscribe(params => {
				this.shipmentDate = new Date(Number(params.shipmentDate));
				this.deliveryAddressCode = params.deliveryAddressCode;

				this.activeOrderService.reloadActiveOrders()
					.pipe(take(1))
					.subscribe(() => {
						this.setupParamsSubscription();
						this.setupShoppingCartSubscription();
					});
			});

		this.allowComments = this.getUser().allowComment;
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	private setupParamsSubscription(): void {
		this.activatedRoute.params
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(params => {
				this.shipmentDate = new Date(Number(params.shipmentDate));
				this.deliveryAddressCode = params.deliveryAddressCode;
				this.getValues(this.deliveryAddressCode, this.shipmentDate);
			});
	}

	private setupShoppingCartSubscription(): void {
		this.shoppingCartService
			.getShoppingCartObservable()
			.pipe(skip(1), takeUntil(this.unsubscribe$))
			.subscribe({
				next: () => this.getValues(this.deliveryAddressCode, this.shipmentDate),
				error: error => this.logger.error('Failed to subscribe to shopping cart', error)
			});
	}

	getUser(): User {
		return this.userService.getUser();
	}

	getCustomerNoReference(): string | null {
		if (this.shoppingCartLines) {
			const reference = this.shoppingCartLines
				.map(shoppingCartLine => shoppingCartLine.referenceNoCustomer)
				.find(reference => reference);

			// First return the reference found in the shopping cart lines
			if (reference) {
				return reference;
			}

			// Next return the reference given in the input field
			if (this.customerReference?.length > 0) {
				return this.customerReference;
			}
		}

		return null;
	}

	getActiveOrder(): ActiveOrder | undefined {
		return this.activeOrderService.getActiveOrderForShipmentDateAndShipToCode(
			this.shipmentDate.getTime(),
			this.deliveryAddressCode
		);
	}

	setOrderAsActiveOrder() {
		this.activeOrderService.setOrderSelectedForShipmentDateAndDeliveryAddress(
			this.shipmentDate,
			this.deliveryAddressCode
		);
	}

	showAddProducts(): boolean {
		return this.userService.isUseFastCheckout() && !this.showBarcode && this.closingInHours > 0;
	}

	handleAddProducts() {
		this.setOrderAsActiveOrder();
		this.router.navigate(['/products']);
	}

	addProductsAllowed(): boolean {
		return this.closingInHours > 0 && this.getActiveOrder()?.addProductsAllowed === true;
	}

	getExpiredAndConfirmedShoppingCartLines(): boolean {
		const expiredShoppingCartLines = this.shoppingCartLines.filter(
			shoppingCartLine => !shoppingCartLine.expired
		);

		const confirmedShoppingCartLines = this.shoppingCartLines.filter(
			shoppingCartLine => shoppingCartLine.confirmed < 0
		);

		return (expiredShoppingCartLines.length === 0 || confirmedShoppingCartLines.length < 1);
	}

	determineIfBarcodeIsEnabled(): void {
		if (this.getUser() &&
			this.shoppingCartLines &&
			this.deliveryDate &&
			this.deliveryAddress
		) {
			this.isBarcodeEnabled = this.getUser().barcodeMethod > 0;
		} else {
			this.isBarcodeEnabled = false;
		}
	}

	/**
	 * Update the barcode sales prices based on the factor and rounding settings
	 *
	 * @param factor
	 * @param rounding
	 */
	updateBarcodeSalesPrices() {
		const rounding = this.factorForm.get('rounding')?.value;
		let factor = this.factorForm.get('factor')?.value;
		factor = factor.replace(',', '.');

		this.barcodeService.updateBarcodeSalesPrice(this.barcodeRows, factor, rounding);
	}

	trackByFunction(index, line) {
		return line ?? null;
	}

	addBarcodeView() {
		if (this.closingInHours === 0) {
			this.showBarcode = false;
			this.applicationService.message = new Message(
				this.orderTranslationsComponent.getOrderClosed(),
				'warning',
				false,
			);
			return;
		}

		this.getShoppingCartLines(this.deliveryAddress?.code, this.shipmentDate)
			.then(() => {
				this.showBarcode = true;
			});
	}

	confirmBarcodes() {
		this.barcodeService
			.addBarcodes(
				this.shoppingCartLines,
				this.orderTranslationsComponent.getBarcodeSuccess(),
				this.orderTranslationsComponent.getBarcodeError()
			)
			.then(() => {
				this.getShoppingCartLines(this.deliveryAddress?.code, this.shipmentDate);
				this.showBarcode = false;
			});
	}

	addBarcode(message: BarCode, line: ShoppingCartLine) {
		line.scBarcodeDescription = message.description;
		line.scBarcode = message.barcode;
		line.scBarcodePrice = message.salesPrice;
	}

	isConfirmAllEnabled() {
		return (
			!this.confirmAllButtonDisabled &&
			!this.showBarcode &&
			this.shoppingCartLines &&
			this.shoppingCartLines.length > 0
		);
	}

	isCustomerReferenceEnabled() {
		// We only receive the customer no reference through the shopping cart lines. If there are no shopping cart lines
		// we cannot determine if there's a customer reference or not, so we disable the input field.
		return this.getCustomerNoReference() === null && this.shoppingCartLines?.length > 0 && this.closingInHours > 0;
	}

	confirmAll() {
		this.confirmAllLoading = true;

		this.shoppingCartService
			.confirmAll(this.shipmentDate.getTime(), this.deliveryAddressCode)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: confirmed => this.handleConfirmAll(confirmed),
				error: error => {
					this.logger.error('Error', error);
				}
			});
	}

	submitReference(): void {
		const referenceInput = this.referenceForm.get('reference')?.value;
		this.activeOrderService
			.updateOrderReference(referenceInput)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(() => (this.customerReference = referenceInput));
	}

	onSortOrderChange(sorting: any) {
		this.sortingKey = sorting.key;
		this.sortingDirection = sorting.direction;
		this.getShoppingCartLines(this.deliveryAddressCode, this.shipmentDate);
	}

	private getValues(deliveryAddressCode: string, shipmentDate: Date) {
		this.deliveryService
			.getDeliveryAddressForCode(deliveryAddressCode)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe({
				next: address => {
					this.deliveryAddress = address;

					const activeOrder = this.getActiveOrder();

					if (activeOrder) {
						this.deliveryDate = activeOrder.deliveryDate;

						this.closingInHours = this.deliveryService.getClosingInHours(activeOrder.closingTime);

						this.closingTimeString = this.deliveryService.getClosingTimeStringForActiveOrder(
							activeOrder.closingTime,
							activeOrder.closingHour,
							this.closingInHours,
							this.orderTranslationsComponent.getClosingInDay(),
							this.orderTranslationsComponent.getClosingInDays(),
							this.orderTranslationsComponent.getClosingTomorrow(),
							this.orderTranslationsComponent.getClosed(),
							this.orderTranslationsComponent.getClosedIn()
						);
					}

					this.getShoppingCartLines(this.deliveryAddressCode, shipmentDate)
						.then(() => this.determineIfBarcodeIsEnabled());
				},
				error: error =>
					this.logger.error('Failed to get delivery address', error)
			});
	}

	private getShoppingCartLines(deliveryAddressCode: string, shipmentDate: Date) {
		let getShoppingCartLinesPromise: Promise<ShoppingCartLine[]>;

		if (this.userService.isUseFastCheckout()) {
			// fast buy method may see all lines in cart, unconfirmed and confirmed
			getShoppingCartLinesPromise = firstValueFrom(
				this.shoppingCartService.getShoppingCart(
					deliveryAddressCode,
					shipmentDate.getTime(),
					this.sortingKey,
					this.sortingDirection
				),
			);
		} else {
			// shopping cart method may only see confirmed lines in pending orders
			getShoppingCartLinesPromise = firstValueFrom(
				this.shoppingCartService.getPendingOrderLines(
					deliveryAddressCode,
					shipmentDate.getTime(),
					this.sortingKey,
					this.sortingDirection
				),
			);
		}

		getShoppingCartLinesPromise.then(lines => {
			this.shoppingCartLines = lines;
			this.confirmAllButtonDisabled = this.getExpiredAndConfirmedShoppingCartLines();
			this.setCalculatedFields();
		});

		return getShoppingCartLinesPromise;
	}

	private getPriceForLine(shoppingCartLine: ShoppingCartLine): number {
		return shoppingCartLine.unitPriceExcResale * shoppingCartLine.quantity;
	}

	private handleConfirmAll(confirmed: boolean) {
		// Hide message if any
		if (this.applicationService.message) {
			this.applicationService.message.isShown = true;
		}

		if (confirmed) {
			this.applicationService.message = new Message(
				this.orderTranslationsComponent.getShoppingCartConfirmed(),
				'info',
				false
			);

			// Before removing the global 'selected order' and 'active shopping cart lines',
			// duplicate the info and add it as local component state.
			// This way the global application state is directly up-to-date
			// and the shopping cart page, still shows all the correct 'confirmed' data.
			this.lastConfirmedActiveOrder = this.activeOrderService.selectedOrder;
			this.lastShoppingCartLines = this.shoppingCartService.activeShoppingCartLines;

			this.activeOrderService.reloadActiveOrders().pipe(take(1)).subscribe();

			this.confirmed = true;
		} else {
			this.applicationService.message = new Message(
				this.orderTranslationsComponent.getShoppingCartError(),
				'info',
				false
			);
		}

		this.confirmAllLoading = false;
	}

	/**
	 * To avoid iterating over the whole set for each calculated field, set them all on load
	 * @private
	 */
	private setCalculatedFields() {
		if (this.shoppingCartLines) {
			// Reset all fields to 0 before calculation
			this.resetTotalFields();

			this.shoppingCartLines.forEach(line => {
				this.totalPrice += this.getPriceForLine(line);
				this.totalVolume += line.volume;
				this.totalVolumeCut += line.volumeCut;
				this.totalVolumeMisc += line.volumeMisc;
				this.totalVolumePlants += line.volumePlants;
			});
		}
	}

	private resetTotalFields() {
		this.totalPrice = 0;
		this.totalVolume = 0;
		this.totalVolumeCut = 0;
		this.totalVolumeMisc = 0;
		this.totalVolumePlants = 0;
	}
}
